import { useMemo } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { breakpoints } from '@frontend/responsiveness';
import { formatters } from '@frontend/shared';
import { useSearch } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import { StatItem, type StatItemData } from '../../components/stat-item';
import { ARRoiConversionChart } from '../charts/ar-roi-conversion-chart';
import { ARRoiNoShowRateChart } from '../charts/ar-roi-no-show-rate-chart';
import { useFetchARROIOOverview } from '../hooks/use-fetch-ar-roi-overview';

export const ARRoiSingleSubView = (props: ReturnType<typeof useFetchARROIOOverview>) => {
  const { t } = useTranslation('analytics');
  const search = useSearch({ strict: false }) ?? {
    view: 'arConversion',
  };
  const { appointmentsCompleted } = props?.aggregated || {};
  const ChartComponent = search.view === 'arConversion' ? ARRoiConversionChart : ARRoiNoShowRateChart;

  const stats: StatItemData[] = useMemo(() => {
    switch (search.view) {
      case 'arConversion':
        return [
          {
            label: t('Appointments were Completed with the help of Appointment Reminders'),
            value: formatters.percent(
              (appointmentsCompleted?.appointmentsCompletedByReminders || 0) /
                (appointmentsCompleted?.totalAppointmentsCompleted || 1)
            ),
          },
        ];
    }

    return [];
  }, [appointmentsCompleted, search.view, t]);

  return (
    <div css={styles.chartContainer}>
      {!!stats.length &&
        stats.map((props) => (
          <StatItem
            {...props}
            css={styles.stats}
            key={props.label}
            subAlignment='bottom'
            valueFontSize={theme.fontSize(66)}
          />
        ))}
      <div style={stats.length ? {} : { width: '100%' }}>
        <ChartComponent {...props} />
      </div>
    </div>
  );
};

const styles = {
  chartContainer: css`
    column-gap: ${theme.spacing(10)};
    display: flex;
    flex-direction: column;
    justify-content: space-evenly;
    row-gap: ${theme.spacing(5)};

    @container (min-width: ${breakpoints.medium.max}px) {
      flex-direction: row;
      align-items: center;
    }
  `,

  stats: css`
    .label {
      width: 100%;
      white-space: normal;
    }
  `,
};
